import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { startCase } from 'shared/utils/startCase';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import {
  TAXONOMY_LEVEL_1,
  TAXONOMY_LEVEL_2,
  TAXONOMY_LEVEL_3,
} from 'product-landing-pages/utils/taxonomyLevels';
import { FeatureFlags } from 'featureFlags/constants';
import { getConsumerSearchServerSideData } from 'server/utils/getConsumerSearchDataServerSide';
import QueryString from 'qs';

interface ICategorizedColorPage extends IProductLandingPage {
  metaDescription?: string;
}

const CategorizedColorPage = ({
  breadcrumbs,
  browseKey,
  browseValue,
  category,
  colorValue,
  constructorResponse,
  consumerSearchFilters,
  displayName,
  globalPricingId,
  isCrawler = false,
  consumerSearchInitialData = '{}',
}: ICategorizedColorPage) => {
  const {
    name = displayName,
    description = '',
    metadataDescription = '',
    titleTag = '',
  } = breadcrumbs || {};

  return (
    <PLPLandingPageComponent
      breadcrumbs={[
        { name: startCase(category), slug: `/${category}` },
        {
          name: startCase(colorValue),
          slug: `/`,
          isStatic: true,
        },
      ]}
      browseKey={browseKey}
      browseValue={browseValue}
      constructorResponse={constructorResponse}
      consumerSearchFilters={consumerSearchFilters}
      consumerSearchInitialData={JSON.parse(consumerSearchInitialData)}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      metadataDescription={metadataDescription}
      metaTitle={titleTag}
      isCrawler={isCrawler}
    />
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const { pageSlug: category, colorValue }: { pageSlug: string; colorValue: string } = query;
  const q = QueryString.parse(query);
  const breadcrumbs = await getBreadcrumbs({
    req,
    res,
    path: `${category}/color/${colorValue}`,
  });
  const isCrawler = isSearchCrawler(req?.headers);
  const isConsumerSearchEnabled =
    req.featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND];

  try {
    const globalPricingId = await fetchGlobalPricingId({ req, res });
    // capitalizes the first letter (accounts for multi-color which should be Multi-color)
    const colorPresentationName: string = colorValue.charAt(0).toUpperCase() + colorValue.slice(1);
    const categoryName: string = startCase(category);
    const displayName = `${colorPresentationName} ${categoryName}`;
    const colorBrowseName: string = colorValue.toLowerCase();
    let consumerSearchFilters: any = {};

    if (TAXONOMY_LEVEL_1.includes(category.toLowerCase())) {
      consumerSearchFilters = {
        colors: colorValue,
        categories: category,
      };
    } else if (TAXONOMY_LEVEL_2.includes(category.toLowerCase())) {
      consumerSearchFilters = {
        colors: colorValue,
        productTypes: category,
      };
    } else if (TAXONOMY_LEVEL_3.includes(category.toLowerCase())) {
      consumerSearchFilters = {
        colors: colorValue,
        activities: category,
      };
    }

    if ((consumerSearchFilters?.productTypes || '').toLowerCase() === 'sneakers') {
      delete consumerSearchFilters.productTypes;
      consumerSearchFilters.categories = 'footwear';
    }

    if (isConsumerSearchEnabled) {
      // If consumer search is enabled, we need to fetch the consumer data details
      const consumerSearchInitialData = await getConsumerSearchServerSideData({
        filters: { ...q, pageFilters: consumerSearchFilters },
      });

      if (!consumerSearchInitialData?.items?.hasData) {
        return { notFound: true };
      }

      return {
        props: {
          ...(await setServerSideTranslations({ locale })),
          breadcrumbs,
          browseKey: 'color',
          browseValue: colorBrowseName,
          category,
          colorValue,
          constructorResponse: {},
          consumerSearchFilters,
          consumerSearchInitialData: JSON.stringify({}),
          displayName,
          globalPricingId,
          isCrawler,
        },
      };
    }

    const colorResponse = await getBrowseResults({
      req,
      res,
      query,
      resolvedUrl,
      filterKey: 'color',
      filterValue: colorBrowseName,
    });

    const invalidSearch = colorResponse.response.total_num_results === 0;

    if (invalidSearch) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        breadcrumbs,
        browseKey: 'color',
        browseValue: colorBrowseName,
        category,
        colorValue,
        constructorResponse: colorResponse,
        consumerSearchFilters,
        displayName,
        globalPricingId,
        isCrawler,
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};

export default CategorizedColorPage;
